// MyCartSlice.js
import { createSlice } from "@reduxjs/toolkit";

const CART_TIMESTAMP_KEY = "cartTimestamp";
const CART_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

const isCartExpired = () => {
  try {
    const ts = localStorage.getItem(CART_TIMESTAMP_KEY);
    if (!ts) return false;
    return Date.now() - Number(ts) > CART_TTL_MS;
  } catch {
    return false;
  }
};

// Function to load state from localStorage
const loadState = () => {
  try {
    const serializedState = localStorage.getItem("cart");
    if (serializedState === null || isCartExpired()) {
      localStorage.removeItem("cart");
      localStorage.removeItem(CART_TIMESTAMP_KEY);
      return [];
    }
    const parsed = JSON.parse(serializedState);
    if (!Array.isArray(parsed)) return [];
    // Filter out poisoned items with NaN/undefined prices
    return parsed.filter((item) => {
      const price = Number(item?.offerprice);
      const qty = Number(item?.qty);
      return item && !isNaN(price) && isNaN(price) === false && !isNaN(qty);
    });
  } catch (e) {
    console.warn("Could not load state from localStorage", e);
    return [];
  }
};

// Function to save state to localStorage
const saveState = (state) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem("cart", serializedState);
    localStorage.setItem(CART_TIMESTAMP_KEY, String(Date.now()));
  } catch (e) {
    console.warn("Could not save state to localStorage", e);
  }
};

const initialState = loadState();

const MyCartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart(state, action) {
      const {
        id, // Use 'id' from payload directly for consistency if it's the unique identifier
        _id, // Keep _id if it's the original mongodb id
        category,
        pName,
        pPrice, // Original Price
        planPrice, // Price for saved total (should be same as pPrice initially)
        pofferprice, // Offer Price
        offerprice, // Price for current total (should be same as pofferprice)
        service,
        pservices,
      } = action.payload;

      const itemIdentifier = id || _id; // Prefer 'id' if present, else fallback to '_id'

      const existingItemIndex = state.findIndex(
        (item) => item.id === itemIdentifier
      );

      if (existingItemIndex !== -1) {
        // Item exists in cart
        const existingItem = state[existingItemIndex];
        if (existingItem.category === category) {
          // Same category, increment quantity
          existingItem.qty += 1;
        } else {
          // Different category, replace the item (this is a specific business rule)
          // If you want to allow multiple categories, you'd add a new item instead.
          state.splice(existingItemIndex, 1, {
            id: itemIdentifier, // Use the consistent identifier
            _id: _id,
            category: category,
            service: service,
            planName: pName,
            planPrice: planPrice || pPrice, // Ensure planPrice is set
            offerprice: offerprice || pofferprice, // Ensure offerprice is set
            pservices: pservices,
            qty: 1, // Reset quantity for the new item
          });
        }
      } else {
        // Item does not exist, add as new
        state.push({
          id: itemIdentifier,
          _id: _id,
          category: category,
          service: service,
          planName: pName,
          planPrice: planPrice || pPrice,
          offerprice: offerprice || pofferprice,
          pservices: pservices,
          qty: 1,
        });
      }
      saveState(state); // Save state after modification
    },

    addToCart1(state, action) {
      // This reducer seems very similar to addToCart.
      // Consider if it can be merged or if the differences are critical.
      // For now, applying similar logic structure.
      const {
        id,
        _id,
        category,
        pName,
        pPrice,
        planPrice,
        pofferprice,
        offerprice,
        service,
        pservices,
      } = action.payload;

      const itemIdentifier = id || _id;

      const existingItemIndex = state.findIndex(
        (item) => item.id === itemIdentifier
      );

      if (existingItemIndex !== -1) {
        const existingItem = state[existingItemIndex];
        if (existingItem.category === category) {
          existingItem.qty += 1;
        } else {
          // Replace logic (as in addToCart)
          state.splice(existingItemIndex, 1, {
            id: itemIdentifier,
            _id: _id,
            category: category,
            service: service,
            planName: pName,
            planPrice: planPrice || pPrice,
            offerprice: offerprice || pofferprice,
            pservices: pservices,
            qty: 1,
          });
        }
      } else {
        state.push({
          id: itemIdentifier,
          _id: _id,
          category: category,
          service: service,
          planName: pName,
          planPrice: planPrice || pPrice,
          offerprice: offerprice || pofferprice,
          pservices: pservices,
          qty: 1,
        });
      }
      saveState(state);
    },

    // This action is intended to DECREMENT quantity by 1
    removeMyCartItem(state, action) {
      // Expects action.payload to be an object like { id: 'someId' }
      const itemIdToRemove = action.payload.id;
      const itemIndex = state.findIndex((item) => item.id === itemIdToRemove);

      if (itemIndex !== -1) {
        if (state[itemIndex].qty > 1) {
          state[itemIndex].qty -= 1;
        }
        // DO NOT remove the item here if qty becomes 1.
        // The component logic will dispatch 'deleteMyCartItem' for complete removal.
      }
      saveState(state);
    },

    // This action is intended to REMOVE the item completely from the cart
    deleteMyCartItem(state, action) {
      // Expects action.payload to be the ID of the item to delete
      const itemIdToDelete = action.payload;
      const newState = state.filter((item) => item.id !== itemIdToDelete);
      saveState(newState); // Save the new state
      return newState; // Return the new state array
    },

    clearCart(state) {
      saveState([]); // Save empty cart to localStorage
      localStorage.removeItem(CART_TIMESTAMP_KEY);
      return []; // Reset state to an empty array
    },
  },
});

export const {
  addToCart,
  addToCart1, // If you still need it separate
  removeMyCartItem, // For decrementing quantity
  deleteMyCartItem, // For removing item completely
  clearCart,
} = MyCartSlice.actions;

export default MyCartSlice.reducer;